import { Key } from "../constants/enums";
import type { AppliedFilters, DateRange } from "../constants/config/filter";
import { resolveRangeToken } from "./dateRange";

type FilterKey = keyof AppliedFilters;

const LAST_LEARNED_FROM_PARAM = "lastLearnedFrom";
const LAST_LEARNED_TO_PARAM = "lastLearnedTo";
const TEACHER_IDS_PARAM = "teacherId";
export const RANGE_PARAM = "range";
export const GROUP_NAME_PARAM = "groupName";
export const LESSON_NAME_PARAM = "lessonName";

export const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const PAGE_SIZE_PARAM = "pageSize";

export const getPageSizeFromQueryParams = (
  searchParams: URLSearchParams,
): number => {
  const raw = Number(searchParams.get(PAGE_SIZE_PARAM));
  return PAGE_SIZE_OPTIONS.includes(raw) ? raw : DEFAULT_PAGE_SIZE;
};

export const setPageSizeInQueryParams = (
  pageSizeGroups: number,
  currentParams: URLSearchParams,
): URLSearchParams => {
  const newParams = new URLSearchParams(currentParams);
  newParams.set(PAGE_SIZE_PARAM, String(pageSizeGroups));
  return newParams;
};

const PAGE_PARAM = "page";

export const getPageFromQueryParams = (
  searchParams: URLSearchParams,
): number => {
  const raw = Number(searchParams.get(PAGE_PARAM));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

export const setPageInQueryParams = (
  page: number,
  currentParams: URLSearchParams,
): URLSearchParams => {
  const newParams = new URLSearchParams(currentParams);
  newParams.set(PAGE_PARAM, String(page));
  return newParams;
};

type QueryParamConfig = {
  paramName: string;
  isArray: boolean;
  isDateRange?: boolean;
  isBoolean?: boolean;
  aliases?: string[];
  nameHintParam?: string;
};

const FILTER_QUERY_CONFIG: Record<FilterKey, QueryParamConfig> = {
  [Key.STUDENT_NAME]: { paramName: "studentId", isArray: true },
  [Key.LESSON_NAME]: {
    paramName: "lessonId",
    isArray: true,
    nameHintParam: LESSON_NAME_PARAM,
  },
  [Key.CLASSROOM_NAME]: {
    paramName: "classroomId",
    isArray: true,
    aliases: ["groupId"],
    nameHintParam: GROUP_NAME_PARAM,
  },
  [Key.TIME_SPENT]: { paramName: "timeSpent", isArray: true },
  [Key.GOALS]: { paramName: "goals", isArray: true },
  [Key.LAST_LEARNED]: {
    paramName: LAST_LEARNED_FROM_PARAM,
    isArray: false,
    isDateRange: true,
  },
  includeNotStarted: {
    paramName: "includeNotStarted",
    isArray: false,
    isBoolean: true,
  },
};

const readArrayParam = (
  searchParams: URLSearchParams,
  config: QueryParamConfig,
): string[] | undefined => {
  const names = [config.paramName, ...(config.aliases ?? [])];
  for (const name of names) {
    const raw = searchParams.get(name);
    if (raw) {
      const values = raw.split(",").filter(Boolean);
      if (values.length > 0) return values;
    }
  }
  return undefined;
};

export const parseFiltersFromQueryParams = (
  searchParams: URLSearchParams,
): AppliedFilters => {
  const filters: AppliedFilters = {};

  Object.entries(FILTER_QUERY_CONFIG).forEach(([key, config]) => {
    const filterKey = key as FilterKey;

    if (config.isDateRange && filterKey === Key.LAST_LEARNED) {
      const from = searchParams.get(LAST_LEARNED_FROM_PARAM);
      const to = searchParams.get(LAST_LEARNED_TO_PARAM);
      if (from && to) {
        filters[filterKey] = { from, to };
      } else {
        const resolved = resolveRangeToken(searchParams.get(RANGE_PARAM));
        if (resolved) {
          filters[filterKey] = resolved;
        }
      }
    } else if (config.isBoolean) {
      const param = searchParams.get(config.paramName);
      if (param !== null) {
        (filters as any)[filterKey] = param === "true";
      }
    } else if (config.isArray) {
      const values = readArrayParam(searchParams, config);
      if (values) {
        (filters as any)[filterKey] = values;
      }
    }
  });

  return filters;
};

/**
 * Drops a `range` token that has no effect: one shadowed by explicit dates
 * (which win in parseFiltersFromQueryParams) or one that names no preset.
 * A live token is left in place (returns null) — it must stay relative.
 */
export const resolveRangeParam = (
  searchParams: URLSearchParams,
): URLSearchParams | null => {
  if (!searchParams.has(RANGE_PARAM)) return null;

  const hasAbsolute =
    searchParams.get(LAST_LEARNED_FROM_PARAM) &&
    searchParams.get(LAST_LEARNED_TO_PARAM);
  if (!hasAbsolute && resolveRangeToken(searchParams.get(RANGE_PARAM))) {
    return null;
  }

  const newParams = new URLSearchParams(searchParams);
  newParams.delete(RANGE_PARAM);
  return newParams;
};

export const getDisplayNameHints = (
  searchParams: URLSearchParams,
): Partial<Record<Key, string>> => {
  const hints: Partial<Record<Key, string>> = {};
  const group = searchParams.get(GROUP_NAME_PARAM);
  const lesson = searchParams.get(LESSON_NAME_PARAM);
  if (group) hints[Key.CLASSROOM_NAME] = group;
  if (lesson) hints[Key.LESSON_NAME] = lesson;
  return hints;
};

export const getTeacherIdsFromQueryParams = (
  searchParams: URLSearchParams,
): number[] => {
  const raw = searchParams.get(TEACHER_IDS_PARAM);
  if (!raw) return [];
  return raw
    .split(",")
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isInteger(value) && value > 0);
};

export const setTeacherIdsInQueryParams = (
  userIds: number[],
  currentParams: URLSearchParams,
): URLSearchParams => {
  const newParams = new URLSearchParams(currentParams);
  if (userIds.length > 0) {
    newParams.set(TEACHER_IDS_PARAM, userIds.join(","));
  } else {
    newParams.delete(TEACHER_IDS_PARAM);
  }
  return newParams;
};

export const hasAnyFilterParams = (searchParams: URLSearchParams): boolean => {
  return Object.keys(parseFiltersFromQueryParams(searchParams)).length > 0;
};

const writeFilterParam = (
  params: URLSearchParams,
  config: QueryParamConfig,
  value: unknown,
): void => {
  if (config.isDateRange) {
    params.delete(LAST_LEARNED_FROM_PARAM);
    params.delete(LAST_LEARNED_TO_PARAM);
    params.delete(RANGE_PARAM);
    if (
      value &&
      typeof value === "object" &&
      "from" in value &&
      "to" in value
    ) {
      const range = value as DateRange;
      // A preset is stored as its token so the link re-resolves against the
      // day it is opened; frozen dates would drop the newest day by tomorrow.
      if (range.preset && resolveRangeToken(range.preset)) {
        params.set(RANGE_PARAM, range.preset);
      } else {
        params.set(LAST_LEARNED_FROM_PARAM, range.from);
        params.set(LAST_LEARNED_TO_PARAM, range.to);
      }
    }
    return;
  }

  if (config.isBoolean) {
    if (value !== undefined) {
      params.set(config.paramName, String(value));
    } else {
      params.delete(config.paramName);
    }
    return;
  }

  if (config.isArray) {
    (config.aliases ?? []).forEach((alias) => params.delete(alias));
    if (config.nameHintParam) params.delete(config.nameHintParam);
    if (Array.isArray(value) && value.length > 0) {
      params.set(config.paramName, value.join(","));
    } else {
      params.delete(config.paramName);
    }
  }
};

export const serializeFiltersToQueryParams = (
  filters: AppliedFilters,
  currentParams: URLSearchParams,
): URLSearchParams => {
  const newParams = new URLSearchParams(currentParams);

  Object.entries(FILTER_QUERY_CONFIG).forEach(([key, config]) => {
    writeFilterParam(newParams, config, (filters as any)[key as FilterKey]);
  });

  return newParams;
};

export const updateFilterInQueryParams = (
  key: FilterKey,
  value: AppliedFilters[FilterKey] | undefined,
  currentParams: URLSearchParams,
): URLSearchParams => {
  const config = FILTER_QUERY_CONFIG[key];
  if (!config) return currentParams;

  const newParams = new URLSearchParams(currentParams);
  writeFilterParam(newParams, config, value);
  return newParams;
};
